import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../utils/supabaseClient';
import {
  normalizeNumericInput,
  getAgeMaxLength,
  shouldMoveFocusFromAge
} from '../utils/numericInput';

// 数値のみを受け付ける入力欄と、その最大桁数
const NUMERIC_FIELD_MAX_LENGTH = {
  age: null, // 年齢は1桁目によって変わるため getAgeMaxLength() で求める
  height: 3,
  weight: 3
};

const getMaxLength = (name, value) =>
  name === 'age' ? getAgeMaxLength(value) : NUMERIC_FIELD_MAX_LENGTH[name];

const Step1 = ({ formData, handleInputChange, setStep, hospitalId, needsSessionStart = false, onStepChange }) => {
  const [isFormValid, setIsFormValid] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const [inputErrors, setInputErrors] = useState({
    age: false,
    height: false,
    weight: false,
    gender: false,
  });
  const [sessionStarted, setSessionStarted] = useState(false);

  const ageRef = useRef(null);
  const heightRef = useRef(null);
  const weightRef = useRef(null);

  // 日本語IMEで変換中かどうか。
  // 変換中に値を書き換えるとIMEの変換が壊れるため、確定するまで正規化を保留する。
  const isComposingRef = useRef(false);

  useEffect(() => {
    const { gender, age, height, weight } = formData;
    setIsFormValid(gender && age && height && weight);
  }, [formData]);

  useEffect(() => {
    sessionStorage.setItem('lastPage', 'Step1');
    
    // セッションリセットチェック
    const isCompletedSession = sessionStorage.getItem('session_completed') === 'true';
    const isPageRefresh = performance.navigation && performance.navigation.type === 1;
    
    // 完了したセッションやページリフレッシュの場合はセッションIDをクリア
    if (isCompletedSession || isPageRefresh) {
      console.log('Step1: セッションが完了しているかページリフレッシュが検出されました。セッションIDをクリアします。');
      sessionStorage.removeItem('session_id');
      sessionStorage.removeItem('session_completed');
      setSessionStarted(false);
    } else {
      // 既存のセッションIDをチェック（ただし、実際のセッション作成はボタン押下時に行う）
      const existingSessionId = sessionStorage.getItem('session_id');
      
      if (existingSessionId) {
        // 既存のセッションIDがあることを記録（ただし、ボタン押下時に新規作成する）
        console.log('Step1: 既存のセッションIDを検出しましたが、ボタン押下時に新しいセッションを作成します');
        // セッション開始フラグはfalseに設定（ボタン押下時に新規作成するため）
        setSessionStarted(false);
      } else {
        console.log('Step1: 既存のセッションIDがありません。needsSessionStartがtrueの場合はボタン押下時に新しいセッションを開始します。');
        setSessionStarted(false);
      }
    }
  }, []);

  // セッションを開始する関数
  const startSession = async () => {
    if (!hospitalId) {
      console.error('Hospital ID is missing for session start');
      return false;
    }

    try {
      // 既存のセッションIDがあれば削除（常に新しいセッションを作成するため）
      const existingSessionId = sessionStorage.getItem('session_id');
      if (existingSessionId) {
        console.log('Step1: 既存のセッションIDを削除し、新しいセッションを作成します');
        sessionStorage.removeItem('session_id');
      }

      console.log('Step1: 新しいセッションを開始します...');

      try {
        // データ登録前に古いセッション関連データをクリア
        sessionStorage.removeItem('session_completed');
        
        const { data, error } = await supabase
          .from('sessions')
          .insert([{ 
            hospital_id: hospitalId
          }])
          .select('id');

        if (error) {
          console.error('Supabase session insert error:', error);
          // エラー時には一時的なUUID形式のIDを使用
          const tempSessionId = `temp-${crypto.randomUUID ? crypto.randomUUID() : Date.now()}`;
          sessionStorage.setItem('session_id', tempSessionId);
          setSessionStarted(true);
          console.log('エラーのため一時的なセッションIDを使用:', tempSessionId);
          return true; // エラーがあってもユーザー体験のために続行
        }

        if (!data || data.length === 0) {
          console.error('セッションデータが返されませんでした');
          const tempSessionId = `temp-${crypto.randomUUID ? crypto.randomUUID() : Date.now()}`;
          sessionStorage.setItem('session_id', tempSessionId);
          setSessionStarted(true);
          console.log('データ不足のため一時的なセッションIDを使用:', tempSessionId);
          return true;
        }

        const sessionId = data[0].id;
        sessionStorage.setItem('session_id', sessionId);
        setSessionStarted(true);
        console.log('Step1: 新しいセッションを作成しました:', sessionId);
        return true;
      } catch (error) {
        console.error('セッション開始エラー:', error);
        // 例外発生時も一時的なUUID形式のIDを使用
        const tempSessionId = `temp-${crypto.randomUUID ? crypto.randomUUID() : Date.now()}`;
        sessionStorage.setItem('session_id', tempSessionId);
        setSessionStarted(true);
        console.log('エラーのため一時的なセッションIDを使用:', tempSessionId);
        return true; // 例外があってもユーザー体験のために続行
      }
    } catch (error) {
      console.error('予期しないエラー:', error);
      return false;
    }
  };

  const moveFocusTo = (ref) => {
    requestAnimationFrame(() => {
      if (!ref.current) return;
      ref.current.focus();
      ref.current.setSelectionRange(0, ref.current.value.length);
    });
  };

  // 数値欄の値を正規化して反映し、必要なら次の欄へフォーカスを移す
  const commitNumericValue = (name, rawValue) => {
    // 全角数字を半角に変換し、数字以外を取り除く（SPEC.md 4.6 仕様1）
    let value = normalizeNumericInput(rawValue);

    // 貼り付けやオートフィルでも桁数を超えないようにする
    // （handleKeyDown はキー入力しか止められないため）
    const maxLength = getMaxLength(name, value);
    if (maxLength && value.length > maxLength) {
      value = value.slice(0, maxLength);
    }

    handleInputChange({ target: { name, value } });

    // フォーカス移動の判定は正規化後の値で行う
    if (name === 'age' && shouldMoveFocusFromAge(value)) {
      moveFocusTo(heightRef);
    } else if (name === 'height' && value.length >= 3) {
      moveFocusTo(weightRef);
    }
  };

  const handleInputChangeWithFocus = (e) => {
    const { name, value } = e.target;

    if (name === 'gender') {
      handleInputChange(e);
      if (value) {
        moveFocusTo(ageRef);
      }
      return;
    }

    // IME変換中はそのまま state に反映し、確定時（compositionend）に正規化する。
    // ここで書き換えるとPCの日本語入力で変換候補が壊れる。
    const composing = isComposingRef.current || (e.nativeEvent && e.nativeEvent.isComposing);
    if (composing) {
      handleInputChange(e);
      return;
    }

    commitNumericValue(name, value);
  };

  const handleCompositionStart = () => {
    isComposingRef.current = true;
  };

  // IMEの変換が確定したタイミングで正規化する（「７０」→「70」など）
  const handleCompositionEnd = (e) => {
    isComposingRef.current = false;
    commitNumericValue(e.target.name, e.target.value);
  };

  const validateInputs = () => {
    const ageValue = parseInt(formData.age, 10);
    const heightValue = parseInt(formData.height, 10);
    const weightValue = parseInt(formData.weight, 10);

    const errors = {
      // 年齢の上限は撤廃した（旧仕様ではFRAXの対応年齢に合わせて90歳を上限としていたが、
      // FRAXを使わなくなったため不要。入力された年齢をそのまま判定に使う）。SPEC.md 4.7(a)
      age: isNaN(ageValue) || ageValue < 40 || formData.age.startsWith('0'),
      height: isNaN(heightValue) || heightValue < 100 || heightValue > 220 || formData.height.startsWith('0'),
      weight: isNaN(weightValue) || weightValue < 25 || weightValue > 125 || formData.weight.startsWith('0'),
      gender: !formData.gender,
    };
    setInputErrors(errors);

    const messages = new Set();
    if (errors.age) {
      messages.add('年齢が40歳以上の方がほねなびのご対象です。それ以外の方は受付にお声がけください。');
    }
    if (errors.height) {
      messages.add('身長は100cmから220cmの間で入力してください。');
    }
    if (errors.weight) {
      messages.add('体重は25kgから125kgの間で入力してください。');
    }
    if (errors.gender) {
      messages.add('性別を選択してください。');
    }
    setErrorMessages(Array.from(messages));

    return !errors.age && !errors.height && !errors.weight && !errors.gender;
  };

  const handleNextStep = async () => {
    if (validateInputs()) {
      // セッション開始が必要な場合は実行
      if (needsSessionStart && !sessionStarted) {
        try {
          const success = await startSession();
          if (!success) {
            console.error('セッション開始に失敗しました');
            // セッション開始に失敗しても続行
          } else if (onStepChange) {
            // セッション開始に成功したらonStepChangeを呼び出し
            onStepChange();
          }
          
          // targettingがfalseの医療機関の場合、Beforehand画面をスキップするため
          // osteoporosisTreatmentとboneDensityTestを明示的にnullに設定
          const { data, error } = await supabase
            .from('hospitals')
            .select('targetting')
            .eq('id', hospitalId)
            .single();
            
          if (!error && data && data.targetting === false) {
            console.log('Step1: targetting=falseの医療機関なので、事前質問の値をnullに設定します');
            handleInputChange({
              target: {
                name: 'osteoporosisTreatment',
                value: null
              }
            });
            handleInputChange({
              target: {
                name: 'boneDensityTest',
                value: null
              }
            });
          }
        } catch (error) {
          console.error('セッション開始中に予期しないエラー:', error);
          // エラーが発生しても続行
        }
      } else if (onStepChange) {
        // セッション開始が不要な場合でもonStepChangeを呼び出し
        onStepChange();
      }
      setStep(3);
    }
  };

  const handleKeyDown = (e) => {
    // 数字入力の長さ制限はキープ（UIの使いやすさのため）
    const { name, value } = e.target;

    // IME変換中はキー入力を止めない（変換操作が行えなくなるため）
    if (isComposingRef.current || (e.nativeEvent && e.nativeEvent.isComposing)) {
      return;
    }

    // 編集・移動系のキーは常に通す
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
      return;
    }

    // 桁数の上限に達していたら入力を受け付けない。
    // 年齢は1桁目が「1」のときだけ3桁まで許可する（100〜199歳）。SPEC.md 4.7(a)
    const maxLength = getMaxLength(name, value);
    if (maxLength && value.length >= maxLength) {
      e.preventDefault();
    }
  };
  
  return (
    <div className="p-4 bg-blue-100 rounded-lg">
      <h2 className="common-title mb-4">ステップ 1</h2>
      <div className="mb-4 text-lg text-left">
        <label className="block mb-2 text-left">性別</label>
        <div className="flex items-center">
          <label className="mr-4">
            <input
              type="radio"
              name="gender"
              value="male"
              checked={formData.gender === 'male'}
              onChange={handleInputChangeWithFocus}
              className="mr-2 w-6 h-6"
            />
            男性
          </label>
          <label>
            <input
              type="radio"
              name="gender"
              value="female"
              checked={formData.gender === 'female'}
              onChange={handleInputChangeWithFocus}
              className="mr-2 w-6 h-6"
            />
            女性
          </label>
        </div>
      </div>
      <div className="mb-4 text-lg text-left">
        <label className="block mb-2 text-left">年齢</label>
        <input 
          type="tel" 
          name="age"
          value={formData.age} 
          onChange={handleInputChangeWithFocus}
          onKeyDown={handleKeyDown}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          inputMode="numeric"
          className={`w-full p-2 border rounded ${inputErrors.age ? 'bg-red-100' : ''}`}
          ref={ageRef}
        />
      </div>
      <div className="mb-4 text-lg text-left">
        <label className="block mb-2 text-left">身長 (cm)</label>
        <input 
          type="tel" 
          name="height"
          value={formData.height} 
          onChange={handleInputChangeWithFocus}
          onKeyDown={handleKeyDown}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          inputMode="numeric"
          className={`w-full p-2 border rounded ${inputErrors.height ? 'bg-red-100' : ''}`}
          ref={heightRef}
        />
      </div>
      <div className="mb-4 text-lg text-left">
        <label className="block mb-2 text-left">体重 (kg)</label>
        <input 
          type="tel" 
          name="weight"
          value={formData.weight} 
          onChange={handleInputChangeWithFocus}
          onKeyDown={handleKeyDown}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          inputMode="numeric"
          className={`w-full p-2 border rounded ${inputErrors.weight ? 'bg-red-100' : ''}`}
          ref={weightRef}
        />
      </div>
      {errorMessages.length > 0 && (
        <div className="text-red-600 text-right text-lg mb-4">
          {errorMessages.map((msg, index) => (
            <p key={index} className="mb-2">{msg}</p>
          ))}
        </div>
      )}
      <br />
      <div className="flex items-center justify-between">
        <button 
          onClick={() => setStep(0)}
          className="bg-gray-500 text-white p-2 rounded hover:bg-gray-600 w-auto"
        >
          前の画面へ戻る
        </button>
        <button 
          onClick={handleNextStep} 
          className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
          disabled={!isFormValid}
        >
          次の質問へ
        </button>
      </div>
    </div>
  );
};

export default Step1; 